import { useState, useEffect } from "react";
import Modal from "../ui/Modal";
import MainSaveButton from "../ui/components/MainSaveButton";
import { FaServer, FaPaperPlane, FaUsersCog, FaSignature, FaEye, FaEyeSlash } from "react-icons/fa";
import { DEFAULT_MAIL_ADMIN_WORKER_ID } from "../../constants/values";
import { sanitizeHtml } from "../../utils/sanitizeHtml";

const INITIAL_FORM = {
  nombre: "",
  color: "",
  textColor: "",
  imapHost: "",
  imapPort: 993,
  imapSecure: true,
  imapProtocol: "ssl",
  imapUser: "",
  imapPassHash: "",
  smtpHost: "",
  smtpPort: 465,
  smtpSecure: false,
  smtpProtocol: "",
  smtpUser: "",
  smtpPassHash: "",
  trabajadoresConAcceso: [DEFAULT_MAIL_ADMIN_WORKER_ID],
  firma: "",
  activo: true,
};

const MailAccountFormModal = ({ open, onClose, account, trabajadores = [], onSave }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showImapPass, setShowImapPass] = useState(false);
  const [showSmtpPass, setShowSmtpPass] = useState(false);

  const isEdit = !!account;

  useEffect(() => {
    if (account) {
      const ids = (account.trabajadoresConAcceso || []).map((t) =>
        typeof t === "string" ? t : t.id
      );
      setForm({
        nombre: account.nombre || "",
        color: account.color || "",
        textColor: account.textColor || "",
        imapHost: account.imapHost || "",
        imapPort: account.imapPort || 993,
        imapSecure: account.imapSecure ?? true,
        imapProtocol: account.imapProtocol || "",
        imapUser: account.imapUser || "",
        imapPassHash: "",
        smtpHost: account.smtpHost || "",
        smtpPort: account.smtpPort || 587,
        smtpSecure: account.smtpSecure ?? false,
        smtpProtocol: account.smtpProtocol || "",
        smtpUser: account.smtpUser || "",
        smtpPassHash: "",
        trabajadoresConAcceso: ids,
        firma: account.firma || "",
        activo: account.activo ?? true,
      });
    } else {
      setForm(INITIAL_FORM);
    }
    setErrors({});
    setShowImapPass(false);
    setShowSmtpPass(false);
  }, [account, open]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const toggleWorker = (workerId) => {
    setForm((prev) => ({
      ...prev,
      trabajadoresConAcceso: prev.trabajadoresConAcceso.includes(workerId)
        ? prev.trabajadoresConAcceso.filter((id) => id !== workerId)
        : [...prev.trabajadoresConAcceso, workerId],
    }));
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validate = () => {
    const errs = {};
    if (!form.nombre.trim()) errs.nombre = "Requerido";
    if (!form.imapHost.trim()) errs.imapHost = "Requerido";
    if (!form.imapPort || form.imapPort <= 0) errs.imapPort = "Puerto inválido";
    if (!form.imapUser.trim()) errs.imapUser = "Requerido";
    if (!isValidEmail(form.imapUser)) errs.imapUser = "Email inválido";
    if (!isEdit && !form.imapPassHash.trim()) errs.imapPassHash = "Requerido";
    if (!form.smtpHost.trim()) errs.smtpHost = "Requerido";
    if (!form.smtpPort || form.smtpPort <= 0) errs.smtpPort = "Puerto inválido";
    if (!form.smtpUser.trim()) errs.smtpUser = "Requerido";
    if (!isValidEmail(form.smtpUser)) errs.smtpUser = "Email inválido";
    if (!isEdit && !form.smtpPassHash.trim()) errs.smtpPassHash = "Requerido";
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (isEdit) {
        if (!payload.imapPassHash) delete payload.imapPassHash;
        if (!payload.smtpPassHash) delete payload.smtpPassHash;
      }
      await onSave(payload, account?.id);
      onClose();
    } catch {
      // parent handles error
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-3 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 ${
      errors[field] ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50"
    }`;

  const labelClass = "text-[11px] font-semibold text-gray-600 mb-0.5 block";
  const errorClass = "text-[10px] text-red-500 mt-0.5";

  return (
    <Modal
      activeModal={open}
      onClose={onClose}
      title={isEdit ? `Editar: ${account.nombre || account.imapUser}` : "Nueva cuenta de correo"}
      className="max-w-4xl"
      themeClass="bg-slate-100"
      centered
      scrollContent
    >
      <div className="flex flex-col gap-5 max-h-[70vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {/* General */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className={labelClass}>Nombre de la cuenta *</label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => handleChange("nombre", e.target.value)}
              placeholder="Ej: Ventas empresa"
              className={inputClass("nombre")}
            />
            {errors.nombre && <p className={errorClass}>{errors.nombre}</p>}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <label className={labelClass}>Color fondo (label)</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.color || "#f97316"}
                  onChange={(e) => handleChange("color", e.target.value)}
                  className="w-10 h-10 rounded-lg cursor-pointer border border-gray-200 p-0.5 bg-white"
                />
                <input
                  type="text"
                  value={form.color || ""}
                  onChange={(e) => handleChange("color", e.target.value)}
                  placeholder="#f97316"
                  className="w-24 px-2 py-1.5 text-[11px] font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Color texto (label)</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.textColor || "#ffffff"}
                  onChange={(e) => handleChange("textColor", e.target.value)}
                  className="w-10 h-10 rounded-lg cursor-pointer border border-gray-200 p-0.5 bg-white"
                />
                <input
                  type="text"
                  value={form.textColor || ""}
                  onChange={(e) => handleChange("textColor", e.target.value)}
                  placeholder="#ffffff"
                  className="w-24 px-2 py-1.5 text-[11px] font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-4">
              <label className="text-[11px] font-semibold text-gray-600">Activo</label>
              <input
                type="checkbox"
                checked={form.activo}
                onChange={(e) => handleChange("activo", e.target.checked)}
                className="w-4 h-4 accent-slate-500 rounded"
              />
            </div>
          </div>
        </div>

        {/* Vista previa del label */}
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">
            Vista previa en la card del email
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className="inline-flex flex-shrink-0 px-1.5 py-0.5 text-[9px] font-medium rounded"
              style={{
                backgroundColor: form.color || "#f97316",
                color: form.textColor || "#ffffff",
              }}
            >
              {form.nombre?.trim() || "Nombre cuenta"}
            </span>
            {form.imapUser?.trim() && (
              <span className="text-[10px] text-gray-500 truncate">
                {form.imapUser}
              </span>
            )}
          </div>
        </div>

        {/* IMAP */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <FaServer className="text-slate-600 text-xs" />
            <h3 className="text-xs font-bold text-gray-700">Configuración IMAP</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Host *</label>
              <input
                type="text"
                value={form.imapHost}
                onChange={(e) => handleChange("imapHost", e.target.value)}
                placeholder="imap.gmail.com"
                className={inputClass("imapHost")}
              />
              {errors.imapHost && <p className={errorClass}>{errors.imapHost}</p>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>Puerto *</label>
                <input
                  type="number"
                  value={form.imapPort}
                  onChange={(e) => handleChange("imapPort", parseInt(e.target.value) || 0)}
                  className={inputClass("imapPort")}
                />
                {errors.imapPort && <p className={errorClass}>{errors.imapPort}</p>}
              </div>
              <div>
                <label className={labelClass}>Protocolo</label>
                <select
                  value={form.imapProtocol}
                  onChange={(e) => handleChange("imapProtocol", e.target.value)}
                  className={inputClass("imapProtocol")}
                >
                  <option value="">— Ninguno —</option>
                  <option value="ssl">SSL</option>
                  <option value="tls">TLS</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Usuario (email) *</label>
              <input
                type="text"
                value={form.imapUser}
                onChange={(e) => handleChange("imapUser", e.target.value)}
                placeholder="ventas@empresa.com"
                className={inputClass("imapUser")}
              />
              {errors.imapUser && <p className={errorClass}>{errors.imapUser}</p>}
            </div>
            <div>
              <label className={labelClass}>Contraseña *{isEdit && " (dejar vacío para no cambiar)"}</label>
              <div className="relative">
                <input
                  type={showImapPass ? "text" : "password"}
                  value={form.imapPassHash}
                  onChange={(e) => handleChange("imapPassHash", e.target.value)}
                  placeholder={isEdit ? "••••••••" : "Contraseña IMAP"}
                  className={inputClass("imapPassHash")}
                />
                <button
                  type="button"
                  onClick={() => setShowImapPass(!showImapPass)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showImapPass ? <FaEyeSlash className="text-xs" /> : <FaEye className="text-xs" />}
                </button>
              </div>
              {errors.imapPassHash && <p className={errorClass}>{errors.imapPassHash}</p>}
            </div>
          </div>
          <label className="flex items-center gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.imapSecure}
              onChange={(e) => handleChange("imapSecure", e.target.checked)}
              className="w-3.5 h-3.5 accent-slate-500 rounded"
            />
            <span className="text-[11px] text-gray-600">Conexión segura (SSL/TLS)</span>
          </label>
        </div>

        {/* SMTP */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <FaPaperPlane className="text-slate-600 text-xs" />
            <h3 className="text-xs font-bold text-gray-700">Configuración SMTP</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Host *</label>
              <input
                type="text"
                value={form.smtpHost}
                onChange={(e) => handleChange("smtpHost", e.target.value)}
                placeholder="smtp.gmail.com"
                className={inputClass("smtpHost")}
              />
              {errors.smtpHost && <p className={errorClass}>{errors.smtpHost}</p>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>Puerto *</label>
                <input
                  type="number"
                  value={form.smtpPort}
                  onChange={(e) => handleChange("smtpPort", parseInt(e.target.value) || 0)}
                  className={inputClass("smtpPort")}
                />
                {errors.smtpPort && <p className={errorClass}>{errors.smtpPort}</p>}
              </div>
              <div>
                <label className={labelClass}>Protocolo</label>
                <select
                  value={form.smtpProtocol}
                  onChange={(e) => handleChange("smtpProtocol", e.target.value)}
                  className={inputClass("smtpProtocol")}
                >
                  <option value="">— Ninguno —</option>
                  <option value="ssl">SSL</option>
                  <option value="tls">TLS</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Usuario (email) *</label>
              <input
                type="text"
                value={form.smtpUser}
                onChange={(e) => handleChange("smtpUser", e.target.value)}
                placeholder="ventas@empresa.com"
                className={inputClass("smtpUser")}
              />
              {errors.smtpUser && <p className={errorClass}>{errors.smtpUser}</p>}
            </div>
            <div>
              <label className={labelClass}>Contraseña *{isEdit && " (dejar vacío para no cambiar)"}</label>
              <div className="relative">
                <input
                  type={showSmtpPass ? "text" : "password"}
                  value={form.smtpPassHash}
                  onChange={(e) => handleChange("smtpPassHash", e.target.value)}
                  placeholder={isEdit ? "••••••••" : "Contraseña SMTP"}
                  className={inputClass("smtpPassHash")}
                />
                <button
                  type="button"
                  onClick={() => setShowSmtpPass(!showSmtpPass)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSmtpPass ? <FaEyeSlash className="text-xs" /> : <FaEye className="text-xs" />}
                </button>
              </div>
              {errors.smtpPassHash && <p className={errorClass}>{errors.smtpPassHash}</p>}
            </div>
          </div>
          <label className="flex items-center gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.smtpSecure}
              onChange={(e) => handleChange("smtpSecure", e.target.checked)}
              className="w-3.5 h-3.5 accent-slate-500 rounded"
            />
            <span className="text-[11px] text-gray-600">Conexión segura (SSL/TLS)</span>
          </label>
        </div>

        {/* Trabajadores con acceso */}
        {/* <div>
          <div className="flex items-center gap-2 mb-2">
            <FaUsersCog className="text-slate-600 text-xs" />
            <h3 className="text-xs font-bold text-gray-700">Trabajadores con acceso</h3>
          </div>
          <p className="text-[10px] text-gray-400 mb-1">
            {form.trabajadoresConAcceso.length} trabajador(es) seleccionado(s)
          </p>
          <div className="max-h-[240px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {trabajadores.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-gray-400">
                No hay trabajadores cargados
              </div>
            ) : (
              trabajadores.map((t) => {
                const isSelected = form.trabajadoresConAcceso.includes(t.id);
                return (
                  <label
                    key={t.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                      isSelected ? "bg-slate-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleWorker(t.id)}
                      className="w-3.5 h-3.5 accent-slate-500 rounded flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">
                        {t.nombre || t.name} {t.apellido || ""}
                      </p>
                      <p className="text-[10px] text-gray-400 truncate">{t.email}</p>
                    </div>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">
                      {t.area || ""}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div> */}

        {/* Firma */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <FaSignature className="text-slate-600 text-xs" />
            <h3 className="text-xs font-bold text-gray-700">Firma (HTML)</h3>
          </div>
          <textarea
            value={form.firma}
            onChange={(e) => handleChange("firma", e.target.value)}
            placeholder='<p><strong>Nombre</strong><br/>Cargo - Empresa<br/>email@ejemplo.com</p>'
            className="w-full min-h-[80px] p-3 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 resize-y bg-gray-50"
            spellCheck={false}
          />
          {form.firma.trim() && (
            <div className="mt-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-1">Vista previa</span>
              <div
                className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(form.firma) }}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2 border-t border-gray-100">
          <MainSaveButton
            text={isEdit ? "Guardar cambios" : "Crear cuenta"}
            onClick={handleSubmit}
            loading={saving}
          />
        </div>
      </div>
    </Modal>
  );
};

export default MailAccountFormModal;
